import Header from "@appComponents/Header/Header";
import { Col, Divider, Row, Space, Table, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import StatsItem from "../Dashboard/parts/StatsItem";
import Tooltip from "@funds/pages/MyFunds/parts/Tooltip";
import ArrowSVG from "./arrow.svg";
import FundsService, { StakingResponse } from "@services/Funds.service";
import { stakingColumns } from "./table-column";
import Big from "big.js";
import NavigationLink from "@funds/components/NavigationLink";
import Chevron from "@images/chevron.svg";

import * as style from "./PendingStakedEthStyle.module.scss"

const PendingStakedEth = ({ location }: any) => {
  const [staking, setStaking] = useState<Array<StakingResponse>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [validatorDaysToActivate, setValidatorDaysToActivate] =
    useState<string>("Unknown");

  useEffect(() => {
    FundsService.getStaking().then((resp) => {
      setStaking(resp.data);
      setIsLoading(false);
    });

    FundsService.stakedEthApr().then((resp) => {
      const hours = resp.data.waiting_time;

      if (hours === "Unknown") {
        setValidatorDaysToActivate("approximately 6 days");
        return;
      }

      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      const daysLabel = days === 1 ? "day" : "days";
      const hoursLabel = remainingHours === 1 ? "hour" : "hours";

      setValidatorDaysToActivate(
        `${days} ${daysLabel} ${
          remainingHours === 0 ? "" : `and ${remainingHours} ${hoursLabel}`
        }`
      );
    });
  }, []);

  const pendingRequests = useMemo(() => {
    return staking.filter((s: StakingResponse) =>
      [
        "queued",
        "processing",
        "provisioned",
        "funded",
        "contract_created",
        "deposited",
      ].includes(s.status)
    );
  }, [staking]);

  const pendingAmount = useMemo(() => {
    return pendingRequests.reduce((acc: Big, curr: StakingResponse) => {
      return Big(acc).plus(curr.amount);
    }, Big(0));
  }, [pendingRequests]);

  const pendingAmountUsd = useMemo(() => {
    return pendingRequests.reduce((acc: Big, curr: StakingResponse) => {
      return Big(acc).plus(curr.total_usd);
    }, Big(0));
  }, [pendingRequests]);

  const path = location.pathname.split("/");
  path.pop();

  return (
    <>
      <Header padding={"50 20 250"} />
      <div className={style.wrapper}>
        <div>
          <NavigationLink
            style={{ marginTop: "40px", display: "inline-block" }}
            to={path.join("/") + "/stakingEth"}
            label={
              <Space align="center">
                <Chevron style={{ transform: "rotate(180deg)" }} />
                &nbsp;
                <span>Back to staking</span>
              </Space>
            }
          />
          <div className={style.headerTitleWrapper}>
            <Typography.Title level={2}>Staking Request List</Typography.Title>
          </div>
          <div className={style.stats}>
            <Row>
              <Col
                xs={24}
                sm={12}
                md={12}
                lg={12}
                xl={6}
                xxl={6}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <StatsItem
                  title={
                    <Typography.Title
                      style={{ textTransform: "uppercase" }}
                      level={5}
                    >
                      Total Pending ETH to be Staked
                    </Typography.Title>
                  }
                  data={[
                    {
                      amount: pendingAmount.toString(),
                      amountUsd: pendingAmountUsd.toString(),
                    },
                  ]}
                  action={null}
                  fieldName={"pending_staked_eth"}
                  className={style.statsItem}
                />
                <Divider
                  type="vertical"
                  style={{ height: "100%", marginRight: "20px" }}
                />
              </Col>
              <Col
                xs={24}
                sm={12}
                md={12}
                lg={12}
                xl={5}
                xxl={5}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <StatsItem
                  title={
                    <Typography.Title
                      style={{ textTransform: "uppercase" }}
                      level={5}
                    >
                      Pending Staking Requests
                    </Typography.Title>
                  }
                  data={[
                    {
                      currency: "",
                      amount: pendingRequests.length.toString(),
                    },
                  ]}
                  action={null}
                  fieldName={"pending_request"}
                  className={style.statsItem}
                  currency={false}
                />
                <Divider
                  type="vertical"
                  style={{ height: "100%", marginRight: "20px" }}
                />
              </Col>
              <Col xs={24} sm={24} md={24} lg={24} xl={13} xxl={13}>
                <Typography.Title
                  style={{ textTransform: "uppercase" }}
                  level={5}
                >
                  Staking PROCESS
                </Typography.Title>

                <div className={style.process}>
                  <Space size={4}>
                    <span>in BF queue</span>

                    <Tooltip
                      title=""
                      tooltipMessage="BF waits until there are 32 ETH in the queue to activate a new validator"
                    />
                  </Space>
                  <ArrowSVG />
                  <Space size={4}>
                    <span>VALIDATOR IN THE QUEUE</span>
                    <Tooltip
                      title=""
                      tooltipMessage={`Current Waiting time for a new Validator to become Active and start earning rewards: around ${validatorDaysToActivate}`}
                    />
                  </Space>
                  <ArrowSVG />
                  <Space size={4}>
                    <span>COMPLETED</span>
                    <Tooltip
                      title=""
                      tooltipMessage="The Validator is active and earning Proof of Stake Rewards. You will now see a Staked ETH balance on your account and start earning ETH rewards every Monday."
                    />
                  </Space>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        <Col xs={{ span: 24 }} lg={{ span: 24 }} className={style.tableWrapper}>
          <Table
            columns={stakingColumns}
            dataSource={staking.reverse()}
            rowKey={"id"}
            scroll={{ x: true, scrollToFirstRowOnChange: true }}
            loading={isLoading}
          />
        </Col>
      </div>
    </>
  );
};

export default PendingStakedEth;
